import { Outlet, createFileRoute } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useTRPC } from "@/integrations/trpc/react";
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext } from "react";

interface BuyerData {
	id: string;
	name: string;
	slug: string;
	address: string | null;
	phone: string | null;
	email: string | null;
}

// Context to provide buyer data to child routes
const PublicBuyerContext = createContext<BuyerData | null>(null);

export const Route = createFileRoute("/public/$slug")({
	// No beforeLoad data fetching - defer to component for client-side fetch
	// This avoids Cloudflare Workers SSR issues with self-referencing HTTP calls
	component: PublicBuyerLayout,
});

export function usePublicBuyerContext(): BuyerData {
	const context = useContext(PublicBuyerContext);
	if (!context) {
		throw new Error("usePublicBuyerContext must be used within PublicBuyerLayout");
	}
	return context;
}

function PublicBuyerLayout() {
	const { slug } = Route.useParams();
	const trpc = useTRPC();

	const { data: buyer, isLoading, error } = useQuery(
		trpc.public.organizations.getBySlug.queryOptions({ slug })
	);

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (error || !buyer) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<div className="text-center">
					<h1 className="text-2xl font-bold mb-2">Organization Not Found</h1>
					<p className="text-muted-foreground">
						{error?.message || "The organization you're looking for doesn't exist."}
					</p>
				</div>
			</div>
		);
	}

	const buyerData: BuyerData = {
		id: buyer.id,
		name: buyer.name,
		slug,
		address: buyer.address,
		phone: buyer.phone,
		email: buyer.email,
	};

	return (
		<PublicBuyerContext.Provider value={buyerData}>
			<Outlet />
		</PublicBuyerContext.Provider>
	);
}
